/**
 * Web Component entry point for the Campaigns Carousel widget.
 *
 * Importing this module registers the <legion-campaigns-carousel> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/campaigns-carousel-web-component';
 *
 * // Then use in HTML:
 * // <legion-campaigns-carousel api-url="https://your-domain.com" api-key="key" />
 * ```
 */

import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

/**
 * Carousel theming attributes use camelCase params directly.
 * The web component attributes are kebab-case for HTML compat,
 * mapped to camelCase query params.
 */
const CAROUSEL_ATTR_TO_PARAM: Record<string, string> = {
  "panel-start": "panelStart",
  "panel-mid": "panelMid",
  "panel-end": "panelEnd",
  "panel-shadow": "panelShadow",
  "title-size": "titleSize",
  "description-size": "descriptionSize",
  "title-shadow": "titleShadow",
  "description-shadow": "descriptionShadow",
};

const CAROUSEL_HTML_ATTRIBUTES = Object.keys(CAROUSEL_ATTR_TO_PARAM);

class LegionCampaignsCarouselElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url",
      
      "max-campaigns",
      "show-expiration",
      "theme",
      "width",
      "height",
      ...CAROUSEL_HTML_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get apiUrl(): string {
    return this.getAttribute("api-url") || window.location.origin;
  }

  private get apiKey(): string | null {
    return this.getAttribute("api-key");
  }

  private get maxCampaigns(): number {
    return parseInt(this.getAttribute("max-campaigns") || "5", 10);
  }

  private get showExpiration(): boolean {
    return this.getAttribute("show-expiration") !== "false";
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "100%";
  }

  private get widgetHeight(): number {
    return parseInt(this.getAttribute("height") || "280", 10);
  }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/campaigns-carousel", this.apiUrl);

    url.searchParams.set("maxCampaigns", String(this.maxCampaigns));
    url.searchParams.set("showExpiration", String(this.showExpiration));
    url.searchParams.set("theme", this.theme);
    url.searchParams.set("height", String(this.widgetHeight));

    if (this.apiKey) {
      url.searchParams.set("apiKey", this.apiKey);
    }

    CAROUSEL_HTML_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(CAROUSEL_ATTR_TO_PARAM[attr], val);
      }
    });

    iframe.src = url.toString();
    iframe.width = this.widgetWidth;
    iframe.height = String(this.widgetHeight);
    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Campaigns Carousel Widget";

    iframe.onload = () => {
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: {
            widget: "campaigns-carousel",
            maxCampaigns: this.maxCampaigns,
            theme: this.theme,
          },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load campaigns carousel widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: ${this.widgetWidth.includes("%") ? this.widgetWidth : this.widgetWidth + "px"};
        height: ${this.widgetHeight}px;
      }
      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 8px;
      }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-campaigns-carousel")
) {
  customElements.define(
    "legion-campaigns-carousel",
    LegionCampaignsCarouselElement
  );
}

export { LegionCampaignsCarouselElement };

